import { useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';
import { BsExclamationTriangle } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Checkbox from '#globalComponents/Checkbox/Checkbox';
import { getErrorMessage } from '#lib/errors/errorMessages';
import { formatCount } from '#lib/numberFormat';
import { useSetMemberLimits } from '#orgFeature/models/org/mutations';

import { CREDIT_TYPES } from '../creditTypes';

const LIMIT_STEP = 10;

const clamp = (value, max) => Math.min(max, Math.max(0, Math.round(Number(value) || 0)));

const LimitField = ({ type, member, quota, value, onChange, renewal }) => {
	const Icon = type.icon;
	const used = member[`${type.key}Used`];
	const unlimited = value === null;
	const id = `limit-${type.key}-${member.userId}`;

	return (
		<div>
			<div className="mb-1.5 flex items-center justify-between">
				<span className="flex items-center gap-1.5 text-xs font-medium text-text">
					<Icon className="h-3.5 w-3.5 text-bgBase-700" />
					{type.label}
				</span>
				<Checkbox
					id={`${id}-none`}
					checked={unlimited}
					onChange={(checked) =>
						onChange(checked ? null : clamp(Math.max(used, Math.round(quota / 4)), quota))
					}
					label={<span className="text-xs text-bgBase-800">Kein Limit</span>}
				/>
			</div>
			<div className={`flex items-center gap-3 ${unlimited ? 'pointer-events-none opacity-40' : ''}`}>
				<input
					type="range"
					aria-label={`${type.label} Monatslimit`}
					min={0}
					max={quota}
					step={LIMIT_STEP}
					value={value ?? 0}
					onChange={(e) => onChange(clamp(e.target.value, quota))}
					className="w-full accent-primary-500"
				/>
				<input
					id={id}
					type="number"
					min={0}
					max={quota}
					value={value ?? ''}
					onChange={(e) => onChange(clamp(e.target.value, quota))}
					className="h-8 w-24 rounded-card border border-bgBase-600 px-2 text-right text-sm tabular-nums focus:border-primary-500 focus:outline-none"
				/>
			</div>
			<div className="mt-1 flex items-center justify-between text-[11px] text-bgBase-800">
				<span>Bereits verbraucht: {formatCount(used)}</span>
				{value !== null && value < used && (
					<span className="flex items-center gap-1 text-caution-700">
						<BsExclamationTriangle className="h-3 w-3" />
						unter Verbrauch – gesperrt bis {renewal ?? 'zur Erneuerung'}
					</span>
				)}
			</div>
		</div>
	);
};

LimitField.propTypes = {
	type: PropTypes.object.isRequired,
	member: PropTypes.object.isRequired,
	quota: PropTypes.number.isRequired,
	value: PropTypes.number,
	onChange: PropTypes.func.isRequired,
	renewal: PropTypes.string,
};

/** Popover to set a member's monthly limits; nothing is moved, the limit only caps what the member may use. */
export const LimitPopover = ({ member, quotas, renewal, anchorRef, onClose }) => {
	const ref = useRef(null);
	const [limits, setLimits] = useState({ company: member.companyLimit, contact: member.contactLimit });
	const limitMutation = useSetMemberLimits();

	useEffect(() => {
		const closeOnOutsideClick = (e) => {
			if (ref.current?.contains(e.target) || anchorRef.current?.contains(e.target)) return;
			onClose();
		};
		document.addEventListener('mousedown', closeOnOutsideClick);
		return () => document.removeEventListener('mousedown', closeOnOutsideClick);
	}, [anchorRef, onClose]);

	const save = () =>
		limitMutation.mutate(
			{ userId: member.userId, companyLimit: limits.company, contactLimit: limits.contact },
			{ onSuccess: onClose },
		);

	return (
		<div
			ref={ref}
			className="absolute right-0 top-full z-20 mt-2 w-[360px] rounded-card border border-bgBase-500 bg-white shadow-xl"
		>
			<div className="px-4 pb-2 pt-3.5">
				<div className="text-sm font-semibold text-text">Monatslimit · {member.fullName || member.email}</div>
				<div className="text-xs text-bgBase-800">
					Obergrenze pro Monat. Ohne Limit nutzt das Mitglied den gemeinsamen Pool frei.
				</div>
			</div>
			<div className="space-y-4 px-4 pb-3">
				{CREDIT_TYPES.map((type) => (
					<LimitField
						key={type.key}
						type={type}
						member={member}
						quota={quotas[type.key]}
						value={limits[type.key]}
						onChange={(value) => setLimits((current) => ({ ...current, [type.key]: value }))}
						renewal={renewal}
					/>
				))}
				{limitMutation.isError && (
					<p className="text-xs text-warning-700">{getErrorMessage(limitMutation.error)}</p>
				)}
			</div>
			<div className="flex items-center justify-between gap-2 rounded-b-card border-t border-bgBase-500 bg-bgBase-300 px-4 py-3">
				<span className="text-[11px] text-bgBase-700">
					Gilt sofort{renewal ? `, Verbrauch setzt sich am ${renewal} zurück.` : '.'}
				</span>
				<div className="flex gap-2">
					<Button variant="outline" size="sm" onClick={onClose}>
						Abbrechen
					</Button>
					<Button size="sm" onClick={save} loading={limitMutation.isPending}>
						Speichern
					</Button>
				</div>
			</div>
		</div>
	);
};

LimitPopover.propTypes = {
	member: PropTypes.object.isRequired,
	quotas: PropTypes.shape({ company: PropTypes.number.isRequired, contact: PropTypes.number.isRequired }).isRequired,
	renewal: PropTypes.string,
	anchorRef: PropTypes.object.isRequired,
	onClose: PropTypes.func.isRequired,
};
